import { useEffect, useRef, useState, useCallback } from "react";
import { X } from "lucide-react";
import { useTheme } from "../../../../ThemeContext/ThemeContext";
import "../Stack.css"; // Import CSS file
import config from '../../../../../Common/config';

export default function LayerList({ onClose, mapview }) {
  const [isCluster, setIsCluster] = useState(false); // Default to cluster mode
  const [visibleLayers, setVisibleLayers] = useState({});
  const layersListRef = useRef(null);
  const { isDarkMode, isLangArab } = useTheme();
  const [language, setLanguage] = useState(isLangArab ? 'arabic' : 'english');

  const updateMapLayers = useCallback((visibleLayers) => {
    if (!mapview || !mapview.map) {
      console.warn("Mapview or map is not initialized");
      return;
    }

    const activeGroup = config.layerListServices[isLangArab ? 'arabic' : 'english'][!isCluster ? 0 : 1];
    const otherGroup = config.layerListServices[isLangArab ? 'arabic' : 'english'][!isCluster ? 1 : 0];

    // Update visibility for active group layers
    activeGroup.children.forEach(layer => {
      const mapLayer = mapview.map.layers.items[0].allSublayers.find(l => l.url === layer.url);
      if (mapLayer) {
        mapLayer.visible = visibleLayers[layer.id] || false;
        mapLayer.parent.visible = true; // Ensure parent layer is visible
        // console.log(`Updated layer ${layer.id} (${layer.url}) to visible: ${mapLayer.visible}`);
      } else {
        console.warn(`Layer not found in map: ${layer.id} (${layer.url})`);
      }
    });

    // Turn off layers from the other group
    otherGroup.children.forEach(layer => {
      const mapLayer = mapview.map.layers.items[0].allSublayers.find(l => l.url === layer.url);
      if (mapLayer) {
        mapLayer.visible = false;
        mapLayer.parent.visible = false;
        // console.log(`Turned off layer ${layer.id} (${layer.url})`);
      }
    });
    console.log("mapview", mapview);
  }, [mapview, isLangArab, isCluster]);

  // Initialize visible layers based on default visibility
  useEffect(() => {
    const initialVisible = {};
    const layers = config.layerListServices[isLangArab ? 'arabic' : 'english'];
    layers.forEach(group => {
      group.children.forEach(child => {
        initialVisible[child.id] = child.visible;
      });
    });
    setVisibleLayers(Object.keys(visibleLayers).length > 1 ? visibleLayers : initialVisible);
    updateMapLayers(Object.keys(visibleLayers).length > 1 ? visibleLayers : initialVisible);
  }, [language, mapview, isLangArab, updateMapLayers]);

  // Handle language change
  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  // Toggle individual layer visibility
  const toggleLayer = (layerId) => {
    setVisibleLayers(prev => {
      const newVisibleLayers = {
        ...prev,
        [layerId]: !prev[layerId]
      };
      console.log(`Toggled layer ${layerId} to ${newVisibleLayers[layerId]}`);
      updateMapLayers(newVisibleLayers);
      return newVisibleLayers;
    });
  };

  // Handle cluster toggle
  const toggleCluster = () => {
    setIsCluster(prev => {
      const newClusterState = !prev;
      console.log(`Switched to ${newClusterState ? 'Cluster' : 'Scale-based'} mode`);

      // Get the current and target groups
      const currentGroup = config.layerListServices[isLangArab ? 'arabic' : 'english'][!prev ? 0 : 1];
      const targetGroup = config.layerListServices[isLangArab ? 'arabic' : 'english'][!newClusterState ? 0 : 1];

      // Create a new visibleLayers object to preserve visibility for matching layers
      const newVisibleLayers = { ...visibleLayers };

      // Map visibility from current group to target group based on layer names
      currentGroup.children.forEach(currentLayer => {
        if (visibleLayers[currentLayer.id]) {
          // Find a layer in the target group with the same name
          const matchingLayer = targetGroup.children.find(targetLayer => targetLayer.name === currentLayer.name);
          if (matchingLayer) {
            newVisibleLayers[matchingLayer.id] = true;
          }
        }
      });

      // Ensure layers in the target group that don't match are turned off (optional, based on your needs)
      targetGroup.children.forEach(targetLayer => {
        if (!currentGroup.children.some(currentLayer => currentLayer.name === targetLayer.name && visibleLayers[currentLayer.id])) {
          newVisibleLayers[targetLayer.id] = false;
        }
      });

      setVisibleLayers(newVisibleLayers);
      updateMapLayers(newVisibleLayers);
      console.log("Updated map layers after cluster toggle", mapview);
      return newClusterState;
    });
  };

  // Get the active group based on cluster toggle and language
  const activeGroup = config.layerListServices[isLangArab ? 'arabic' : 'english'][!isCluster ? 0 : 1];

  // Render layer toggles
  const renderLayers = () => {
    return activeGroup.children.map(layer => (
      <div key={layer.id} className="layer-item">
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            className="hidden peer"
            checked={visibleLayers[layer.id] || false}
          // onChange={() => toggleLayer(layer.id)}
          />
          <span
            className={`${isLangArab ? "ml-2" : "mr-2"
              } h-4 w-4 rounded-sm border border-gray-400 bg-white peer-checked:bg-[#69A9C2] 
                                 flex items-center justify-center transition-colors duration-300 cursor-pointer`}
            onClick={() => toggleLayer(layer.id)}
          >
            {(visibleLayers[layer.id]) && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 font-bold text-white"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 00-1.414 0L8 12.586 4.707 9.293a1 1 0 00-1.414 1.414l4 4a1 1 0 001.414 0l8-8a1 1 0 000-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </span>
          <span>{layer.name}</span>
        </label>
      </div>
    ));
  };

  return (
    <div dir={isLangArab ? "rtl" : "ltr"} className="flex items-center justify-center z-10">
      <div
        ref={layersListRef}
        className={`fixed ${isLangArab ? "sm:left-12 left-0" : "sm:right-12 right-0"} top-14 laptop_s:top-20 h-[95%] sm:h-[430px] p-4 rounded-lg shadow-lg w-full sm:w-96 transition-colors duration-300
          ${isDarkMode ? "bg-[rgba(96,96,96,0.8)] text-white" : "bg-white bg-opacity-95 text-black"}`}
      >
        <div className="w-full flex justify-between items-center">
          <div className="p-1">
            <h1 className={`text-[16px] font-medium ${isLangArab ? "text-right" : "text-left"}`}>
              {isLangArab ? "قائمة الطبقات" : "Layer List"}
            </h1>
          </div>
          <div className="gap-2 flex justify-center items-center">
            <div onClick={toggleCluster} className="flex gap-1 justify-center items-center">
              <h2 className={`font-500 text-[16px] flex justify-center items-center ${isDarkMode ? "text-white" : "text-black"}`}>
                {isLangArab ? "التجميع" : "Cluster"}
              </h2>
              <div
                className={`relative h-4 w-9 flex items-center rounded-full p-1 shadow-md cursor-pointer ${isDarkMode ? 'bg-[#D9D9D9]' : 'bg-[#D9D9D9]'}`}
              >
                <button
                  className={`absolute ${!isCluster ? (isLangArab ? "left-0.5" : "left-1") : (isLangArab ? "right-0.5" : "right-1")} bg-[#1A6162] w-3 h-3 rounded-full shadow-lg transform duration-300 ease-in-out flex justify-center items-center`}
                >
                  <img
                    src=""
                    alt=""
                    className="w-7"
                  />
                </button>
                <h3 className={`absolute ${!isCluster ? (isLangArab ? "right-0.5 text-[7px]" : "right-0.5 text-[9px]") : (isLangArab ? "left-0.5 text-[7px]" : "left-1 text-[10px]")} flex justify-center items-center ${isDarkMode ? "text-black" : "text-[#737373]"} font-500`}>
                  {!isCluster ? (isLangArab ? "اطفاء" : "OFF") : (isLangArab ? "تشغيل" : "ON")}
                </h3>
              </div>
            </div>
            <button
              className={` ${isDarkMode ? "text-[#FFFFFFFF] text-opacity-80" : "text-gray-800"}`}
              onClick={onClose}
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>
        <div
          className={`my-2 h-[1px] w-full transition-colors duration-300 ${isDarkMode ? "bg-white bg-opacity-50" : "bg-black bg-opacity-20"}`}
        ></div>
        <div>
          <div>
            {renderLayers()}
          </div>
        </div>
      </div>
    </div>
  );
};